import React from "react";

const reasons = [
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    title: "Fast Delivery",
    description: "Optimized routing and carrier partnerships ensure your cargo reaches its destination in the shortest possible time, every time.",
    color: "from-orange-400 to-orange-600",
    bg: "bg-orange-50",
    text: "text-orange-600",
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    ),
    title: "Secure Handling",
    description: "Advanced cargo tracking, tamper-proof packaging solutions, and insured freight options keep your goods protected at every stage.",
    color: "from-blue-400 to-blue-600",
    bg: "bg-[#1c7abb]/10",
    text: "text-[#1c7abb]",
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    title: "24/7 Support",
    description: "Our expert team is available around the clock — no matter the time zone — to assist with inquiries, exceptions, and real-time updates.",
    color: "from-green-400 to-green-600",
    bg: "bg-green-50",
    text: "text-green-600",
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: "Cost Effective",
    description: "Competitive pricing with no hidden fees. Our volume-based purchasing power allows us to pass meaningful savings on to every client.",
    color: "from-purple-400 to-purple-600",
    bg: "bg-purple-50",
    text: "text-purple-600",
  },
];

export default function WhyChooseUs() {
  return (
    <section className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#0f522d]/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-900/30 rounded-full blur-3xl pointer-events-none" />

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16 animate-on-scroll">
          <span className="text-[#1c7abb] font-semibold text-sm uppercase tracking-widest">Our Advantages</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mt-3 mb-5">
            Why Businesses Choose <span className="text-[#1c7abb]">Load2Serve</span>
          </h2>
          <p className="max-w-xl mx-auto text-slate-400 leading-relaxed">
            We go beyond moving cargo — we deliver confidence, reliability, and measurable results at every step of your supply chain.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((reason, i) => (
            <div
              key={reason.title}
              className={`animate-on-scroll stagger-${i + 1} group bg-slate-800/60 hover:bg-slate-800 border border-slate-700 hover:border-slate-600 rounded-2xl p-7 transition-all duration-300 hover:-translate-y-2`}
            >
              {/* Icon */}
              <div className={`w-16 h-16 rounded-2xl ${reason.bg} bg-opacity-10 flex items-center justify-center ${reason.text} mb-6`}>
                {reason.icon}
              </div>

              <h3 className="text-lg font-bold text-white mb-3">{reason.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{reason.description}</p>

              {/* Bottom accent line */}
              <div className={`mt-6 h-0.5 w-0 group-hover:w-full transition-all duration-500 rounded-full bg-gradient-to-r ${reason.color}`} />
            </div>
          ))}
        </div>

        {/* Bottom trust bar */}
        <div className="mt-16 animate-on-scroll">
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl px-8 py-6 flex flex-wrap justify-center gap-8 items-center">
            {["ISO 9001:2015 Certified", "IATA Accredited", "WCA Member", "FIATA Member", "Lloyd's Insured"].map((badge) => (
              <div key={badge} className="flex items-center gap-2 text-slate-300">
                <svg className="w-4 h-4 text-[#1c7abb]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">{badge}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
